import { useState, useEffect } from 'react';
import { Sheet, Field, Input, Select, PrimaryBtn, GhostBtn, IconBtn, Icons } from './UI';
import { TEAM, today, nowISO, generateTimeOptions, formatTime, linkify } from '../utils/constants';
import { api } from '../utils/api';

const TIME_OPTIONS = generateTimeOptions();

function defaultTimes() {
  const d = new Date();
  const h = d.getHours();
  const m = Math.floor(d.getMinutes() / 15) * 15;
  const pad = n => String(n).padStart(2, '0');
  const startTime = `${pad(h)}:${pad(m)}`;
  const endH = h + (m + 60 >= 60 ? 1 : 0);
  const endM = (m + 60) % 60;
  const endTime = `${pad(Math.min(endH, 23))}:${pad(endM)}`;
  return { startTime, endTime };
}

function LogForm({ initial, currentUser, onSave, onClose }) {
  const times = defaultTimes();
  const [date, setDate] = useState(initial?.date || today());
  const [startTime, setStartTime] = useState(initial?.startTime || times.startTime);
  const [endTime, setEndTime] = useState(initial?.endTime || times.endTime);
  const [note, setNote] = useState(initial?.note || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!note.trim()) { alert('Please add a note about what you worked on.'); return; }
    setSaving(true);
    await onSave({ date, startTime, endTime, note: note.trim() });
    setSaving(false);
  };

  return (
    <Sheet title={initial ? 'Edit Log Entry' : 'Log Hours'} onClose={onClose} footer={<><GhostBtn onClick={onClose}>Cancel</GhostBtn><PrimaryBtn onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : initial ? 'Save Changes' : 'Save Entry'}</PrimaryBtn></>}>
      <Field label="Date">
        <Input type="date" value={date} onChange={e => setDate(e.target.value)} />
      </Field>
      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="Start Time">
          <Select value={startTime} onChange={e => setStartTime(e.target.value)}>
            {TIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </Field>
        <Field label="End Time">
          <Select value={endTime} onChange={e => setEndTime(e.target.value)}>
            {TIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </Field>
      </div>
      <Field label="What did you work on?">
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={5} placeholder="Describe what you worked on during this time..." style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: '#0f172a', outline: 'none', width: '100%', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} autoFocus />
      </Field>
    </Sheet>
  );
}

export function LogTab({ store, setStore, currentUser, newActionRef }) {
  const [filterPerson, setFilterPerson] = useState(currentUser || 'All');
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  useEffect(() => { if (newActionRef) newActionRef.current = { task: () => setShowForm(true) }; }, [newActionRef]);

  const entries = store.logEntries || [];

  const filtered = filterPerson === 'All' ? entries : entries.filter(e => e.person === filterPerson);
  const sorted = [...filtered].sort((a, b) => b.date.localeCompare(a.date) || b.startTime.localeCompare(a.startTime));

  // Group by date
  const groups = {};
  sorted.forEach(e => { if (!groups[e.date]) groups[e.date] = []; groups[e.date].push(e); });
  const dates = Object.keys(groups).sort((a, b) => b.localeCompare(a));

  const saveEntry = async ({ date, startTime, endTime, note }) => {
    const isNew = !editingEntry;
    const entry = {
      id: isNew ? store.logNextId : editingEntry.id,
      person: currentUser || '',
      date, startTime, endTime, note,
      createdAt: nowISO(),
    };
    setStore(s => ({
      ...s,
      logNextId: isNew ? s.logNextId + 1 : s.logNextId,
      logEntries: isNew ? [...(s.logEntries || []), entry] : s.logEntries.map(e => e.id === entry.id ? entry : e),
    }));
    setShowForm(false);
    setEditingEntry(null);
    await api.saveLogEntry(entry);
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this log entry?')) return;
    setStore(s => ({ ...s, logEntries: s.logEntries.filter(e => e.id !== id) }));
    await api.deleteLogEntry(id);
  };

  return (
    <>
      {/* Filter */}
      <div style={{ paddingBottom: 12 }}>
        <select value={filterPerson} onChange={e => setFilterPerson(e.target.value)} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#0f172a', borderRadius: 8, padding: '7px 10px', fontSize: 13, width: '100%' }}>
          <option value="All">All Team Members</option>
          {TEAM.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>

      {/* Entries */}
      <div style={{ paddingBottom: 80 }}>
        {dates.length === 0 && (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: '48px 20px' }}>No log entries yet. Hit "+ Log Hours" to add your first entry.</div>
        )}
        {dates.map(date => {
          const dayEntries = groups[date];
          const d = new Date(date + 'T12:00:00');
          const dayLabel = d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
          const totalMins = dayEntries.reduce((sum, e) => {
            const [sh, sm] = e.startTime.split(':');
            const [eh, em] = e.endTime.split(':');
            const mins = (parseInt(eh) * 60 + parseInt(em)) - (parseInt(sh) * 60 + parseInt(sm));
            return sum + (mins > 0 ? mins : 0);
          }, 0);
          const hrs = Math.floor(totalMins / 60);
          const mins = totalMins % 60;
          const totalStr = `${hrs}${mins > 0 ? `:${String(mins).padStart(2, '0')}` : ''}h`;

          return (
            <div key={date} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.8px', padding: '8px 4px 6px', borderBottom: '2px solid #e2e8f0', marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                <span>{dayLabel}</span>
                <span style={{ color: '#3b82f6' }}>{totalStr} logged</span>
              </div>
              {dayEntries.map(e => (
                <div key={e.id} style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', marginBottom: 6, boxShadow: '0 1px 3px rgba(0,0,0,.06)', borderLeft: '3px solid #3b82f6', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#3b82f6', whiteSpace: 'nowrap', minWidth: 110, paddingTop: 2 }}>{formatTime(e.startTime)} – {formatTime(e.endTime)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 2 }}>{e.person}</div>
                    <div style={{ fontSize: 14, color: '#0f172a', lineHeight: 1.5, whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: linkify(e.note) }} />
                  </div>
                  {e.person === currentUser && (
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <IconBtn onClick={() => setEditingEntry(e)} title="Edit"><Icons.Edit /></IconBtn>
                      <IconBtn onClick={() => deleteEntry(e.id)} title="Delete" color="#ef4444"><Icons.X /></IconBtn>
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {showForm && <LogForm currentUser={currentUser} onSave={saveEntry} onClose={() => setShowForm(false)} />}
      {editingEntry && <LogForm initial={editingEntry} currentUser={currentUser} onSave={saveEntry} onClose={() => setEditingEntry(null)} />}
    </>
  );
}

